import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Star, ChevronDown, ChevronRight, Smile } from "lucide-react";

interface SentimentComment { reviewerName: string | null; rating: number; comment: string; createdAt: string }
interface ResidentSentimentRow { memberId: number; name: string | null; avgRating: number | null; reviewCount: number; comments: SentimentComment[] }

function Stars({ value }: { value: number | null }) {
  return (
    <span className="inline-flex items-center">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} className={`h-3.5 w-3.5 ${value != null && n <= Math.round(value) ? "fill-amber-400 text-amber-400" : "text-gray-300"}`} />
      ))}
    </span>
  );
}

// Host-side "Resident Sentiment" panel — surfaces the housemate-to-housemate
// peer reviews collected in the member portal (average received rating + the
// comments left about each resident). Sorted lowest-rated first so a struggling
// housemate stands out. Members never see who rated them; the host does.
export function ResidentSentiment({ propertyId }: { propertyId: number }) {
  const [expanded, setExpanded] = useState<number | null>(null);
  const { data } = useQuery<{ residents: ResidentSentimentRow[] }>({
    queryKey: ["/api/app/properties", propertyId, "peer-reviews"],
    queryFn: async () => {
      const res = await fetch(`/api/app/properties/${propertyId}/peer-reviews`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load resident sentiment");
      return res.json();
    },
  });
  const residents = data?.residents ?? [];

  return (
    <div>
      <h3 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900">
        <Smile className="h-5 w-5 text-brand" /> Resident Sentiment
      </h3>
      {residents.length === 0 ? (
        <p className="rounded-lg border bg-gray-50 p-3 text-sm text-gray-400">No peer reviews yet. Residents can rate housemates from their portal.</p>
      ) : (
        <ul className="divide-y rounded-lg border">
          {residents.map((r) => (
            <li key={r.memberId}>
              <button
                className="flex w-full items-center justify-between gap-2 p-3 text-left hover:bg-gray-50"
                onClick={() => setExpanded((v) => (v === r.memberId ? null : r.memberId))}
                data-testid={`resident-sentiment-${r.memberId}`}
              >
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium text-gray-900">{r.name || `Member ${r.memberId}`}</span>
                  <span className="mt-0.5 flex items-center gap-2">
                    <Stars value={r.avgRating} />
                    <span className="text-xs text-gray-500">{r.avgRating != null ? r.avgRating.toFixed(1) : "—"} · {r.reviewCount} rating{r.reviewCount === 1 ? "" : "s"}</span>
                  </span>
                </span>
                {r.comments.length > 0 && (expanded === r.memberId ? <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" /> : <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />)}
              </button>
              {expanded === r.memberId && r.comments.length > 0 && (
                <ul className="space-y-2 bg-gray-50 px-3 pb-3">
                  {r.comments.map((c, i) => (
                    <li key={i} className="rounded-md border bg-white p-2 text-xs">
                      <div className="mb-0.5 flex items-center gap-2">
                        <Stars value={c.rating} />
                        <span className="text-gray-400">{c.reviewerName || "A housemate"}</span>
                      </div>
                      <p className="whitespace-pre-wrap break-words text-gray-700">{c.comment}</p>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
